/**
 * Gold999 — Lingotto Progressivo (custom element).
 *
 * Sagoma: placeholder piatto V3.2, approvato in attesa dell'asset grafico
 * esterno definitivo. La geometria e la logica di riempimento/
 * sincronizzazione qui sotto sono quelle già validate: NON modificarle in
 * isolamento da questo file senza rivedere anche il prototipo.
 *
 * Attributi: current-grams, target-grams (obbligatori), grams-decimals,
 * percent-decimals (opzionali, default 2/1), duration-ms (default 3500).
 * Nessuna dipendenza esterna, nessuna chiamata di rete: i valori vengono
 * letti solo dagli attributi HTML presenti sul tag.
 *
 * Colori adattivi (contorno + parte non riempita): letti da due custom
 * property ereditate dalla pagina ospitante (--g999-outline,
 * --g999-empty-fill/--g999-empty-opacity), con fallback identici ai
 * colori originali se la pagina non li definisce — così il componente
 * resta corretto anche fuori da Shopify (es. Gold999 Engine).
 *
 * La tipografia del riempimento numerico (part="grams"/"percent") è
 * intenzionalmente NON stilizzata qui: viene ereditata dal CSS della
 * pagina ospitante tramite ::part(), così il componente resta agnostico
 * rispetto al tema (Shopify oggi, Gold999 Engine in futuro).
 */
(function () {
  function easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }
  function fmt(n, decimals) {
    return n.toLocaleString('it-IT', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  }

  var TEMPLATE =
    '' +
    '<style>' +
    ':host{ display:block; }' +
    'svg{ display:block; width:min(94vw, 520px); height:auto; overflow:visible; margin-inline:auto; }' +
    '.readout{ text-align:center; margin-block-start:22px; }' +
    '.g999-grams{ font-variant-numeric:tabular-nums; }' +
    '.g999-sep{ padding-inline:.18em; opacity:.6; }' +
    '.g999-percent{ margin-block-start:8px; }' +
    '</style>' +
    '<svg viewBox="0 0 760 200" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Lingotto illustrato che si riempie d\'oro">' +
    '<defs>' +
    '<filter id="groundShadow" x="-30%" y="-100%" width="160%" height="300%">' +
    '<feGaussianBlur stdDeviation="5"/>' +
    '</filter>' +
    '<clipPath id="silhouette">' +
    '<rect x="60" y="48" width="640" height="92" rx="24"/>' +
    '</clipPath>' +
    '<clipPath id="fillClip">' +
    '<rect id="fillRect" x="60" y="40" width="0" height="112"/>' +
    '</clipPath>' +
    '</defs>' +
    '<ellipse cx="380" cy="163" rx="228" ry="7" fill="#0F1720" opacity=".13" filter="url(#groundShadow)"/>' +
    '<g clip-path="url(#silhouette)">' +
    '<rect x="60" y="48" width="640" height="92" style="fill:var(--g999-empty-fill,#F2EDDF);fill-opacity:var(--g999-empty-opacity,.4)"/>' +
    '<rect x="140" y="55" width="46" height="64" rx="14" fill="#FFFFFF" opacity=".32" transform="skewY(-18)"/>' +
    '<rect x="562" y="52" width="18" height="58" rx="8" fill="#FFFFFF" opacity=".26" transform="skewY(-18)"/>' +
    '</g>' +
    '<g clip-path="url(#silhouette)">' +
    '<g clip-path="url(#fillClip)">' +
    '<rect x="60" y="48" width="640" height="92" fill="#C6A75E"/>' +
    '<rect x="110" y="60" width="88" height="18" rx="9" fill="#FBF3DD" opacity=".8"/>' +
    '<rect id="sweep" x="-60" y="30" width="26" height="110" rx="8" fill="#FFFFFF" opacity="0" transform="rotate(18 0 90)"/>' +
    '<rect id="meniscus" x="55" y="40" width="12" height="112" rx="6" fill="#FBF3DD" opacity="0"/>' +
    '</g>' +
    '</g>' +
    '<rect x="60" y="122" width="640" height="12" fill="#0F1720" opacity=".12" clip-path="url(#silhouette)"/>' +
    '<g fill="none" style="stroke:var(--g999-outline,#0F1720)" stroke-width="8" stroke-linejoin="round" stroke-linecap="round">' +
    '<rect x="60" y="48" width="640" height="92" rx="24"/>' +
    '</g>' +
    '</svg>' +
    '<div class="readout">' +
    '<div part="grams" class="g999-grams">' +
    '<span id="curGrams">0,00</span> g<span part="sep" class="g999-sep">/</span><span id="targetGrams">10</span> g' +
    '</div>' +
    '<div id="percentLine" part="percent" class="g999-percent">0,0% del tuo lingotto</div>' +
    '</div>';

  var FILL_MIN_X = 60,
    FILL_SPAN = 640; // 700 - 60 — placeholder piatto V3.2, invariato

  function GoldIngot() {
    var el = Reflect.construct(HTMLElement, [], GoldIngot);
    var root = el.attachShadow({ mode: 'open' });
    root.innerHTML = TEMPLATE;
    el._root = root;
    el._fillRect = root.getElementById('fillRect');
    el._meniscus = root.getElementById('meniscus');
    el._sweep = root.getElementById('sweep');
    el._curEl = root.getElementById('curGrams');
    el._targetEl = root.getElementById('targetGrams');
    el._percentEl = root.getElementById('percentLine');
    el._played = false;
    return el;
  }
  GoldIngot.prototype = Object.create(HTMLElement.prototype);
  GoldIngot.prototype.constructor = GoldIngot;

  GoldIngot.prototype.connectedCallback = function () {
    var target = parseFloat(this.getAttribute('target-grams') || '10');
    this._targetEl.textContent = fmt(target, target % 1 === 0 ? 0 : 2);
    this._renderStatic();

    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      this._setProgress(1);
      return;
    }

    var self = this;
    if ('IntersectionObserver' in window) {
      this._io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting && !self._played) {
              self._played = true;
              self.play();
            }
          });
        },
        { threshold: 0.4 }
      );
      this._io.observe(this);
    } else {
      this.play();
    }
  };

  GoldIngot.prototype._renderStatic = function () {
    var gDec = parseInt(this.getAttribute('grams-decimals') || '2', 10);
    this._curEl.textContent = fmt(0, gDec);
    this._percentEl.textContent = fmt(0, parseInt(this.getAttribute('percent-decimals') || '1', 10)) + '% del tuo lingotto';
  };

  GoldIngot.prototype._setProgress = function (t) {
    var current = parseFloat(this.getAttribute('current-grams') || '0');
    var target = parseFloat(this.getAttribute('target-grams') || '10');
    var gDec = parseInt(this.getAttribute('grams-decimals') || '2', 10);
    var pDec = parseInt(this.getAttribute('percent-decimals') || '1', 10);

    var displayed = current * t;
    var percent = target > 0 ? (displayed / target) * 100 : 0;
    var widthPx = FILL_SPAN * (displayed / target);

    this._fillRect.setAttribute('width', Math.max(0, widthPx));

    var menX = FILL_MIN_X + widthPx - 6;
    this._meniscus.setAttribute('x', menX);
    this._meniscus.style.opacity = t <= 0.002 || t >= 0.999 ? '0' : '.85';

    // Piccola libertà creativa: un riflesso illustrato attraversa l'oro
    // mentre cresce — deriva anch'esso da t, nessun secondo timer.
    var sweepX = FILL_MIN_X - 80 + t * (FILL_SPAN + 160);
    var sweepOpacity = Math.max(0, 1 - Math.abs(t - 0.5) * 2.6) * 0.3;
    this._sweep.setAttribute('transform', 'translate(' + sweepX + ',0) rotate(18 0 90)');
    this._sweep.setAttribute('opacity', sweepOpacity.toFixed(2));

    this._curEl.textContent = fmt(displayed, gDec);
    this._percentEl.textContent = fmt(percent, pDec) + '% del tuo lingotto';
  };

  GoldIngot.prototype.play = function () {
    var self = this;
    var duration = parseFloat(this.getAttribute('duration-ms') || '3500');
    var start = null;
    if (this._raf) cancelAnimationFrame(this._raf);

    function frame(ts) {
      if (start === null) start = ts;
      var t = Math.min(1, (ts - start) / duration);
      self._setProgress(easeOutCubic(t));
      if (t < 1) {
        self._raf = requestAnimationFrame(frame);
      }
    }
    this._setProgress(0);
    this._raf = requestAnimationFrame(frame);
  };

  customElements.define('gold999-ingot-progress', GoldIngot);
})();
